import type { SiteResponse } from '@lms/api-client/backend';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { Textarea } from '@lms/ui/textarea';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { useTranslations } from 'use-intl';
import { SimpleSelect } from '@/components/simple-select';
import { SubmitButton } from '@/components/submit-button';
import { useBrand } from '@/features/branding/brand-provider';
import { addSiteTemplate } from './site-templates-api';

// Ports _components/add-template.tsx. The conform + server-action form becomes a
// controlled form calling `addSiteTemplate`; on success it revalidates the list
// SWR key and navigates back (the Next action `redirect`ed).
export function AddTemplateForm({ locations }: { locations: SiteResponse[] }) {
  const t = useTranslations('admin');
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const { slug } = useBrand();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [siteId, setSiteId] = useState('');
  const [pending, setPending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    site_id?: string;
    description?: string;
  }>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setFieldErrors({});
    setFormErrors([]);
    try {
      const result = await addSiteTemplate({
        name,
        site_id: siteId,
        description: description === '' ? undefined : description,
      });
      if (result.status === 'success') {
        await mutate('admin/site-templates');
        navigate(`/${slug}/admin/templates`);
        return;
      }
      setFieldErrors(result.fieldErrors ?? {});
      setFormErrors(result.formErrors ?? []);
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="name">{t('name')}</Label>
        <Input
          id="name"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {fieldErrors.name && (
          <p className="text-destructive text-sm">{fieldErrors.name}</p>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="description">{t('description')}</Label>
        <Textarea
          id="description"
          name="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {fieldErrors.description && (
          <p className="text-destructive text-sm">{fieldErrors.description}</p>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="site_id">{t('site')}</Label>
        <SimpleSelect
          id="site_id"
          name="site_id"
          placeholder="Välj sida till mall"
          value={siteId}
          onValueChange={setSiteId}
          options={locations.map((l) => ({ value: `${l.id}`, label: l.name }))}
        />
        {fieldErrors.site_id && (
          <p className="text-destructive text-sm">{fieldErrors.site_id}</p>
        )}
      </div>
      {formErrors.length > 0 && (
        <div className="text-destructive text-sm">
          {formErrors.map((error) => (
            <p key={error}>{error}</p>
          ))}
        </div>
      )}
      <SubmitButton label="Add" pending={pending} />
    </form>
  );
}
